import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import supportInnovationApi from '../services/support-innovation';
import permissionsApi from '../services/permissions';
import { PAGES } from './Permissions';
import SupportInnovationWizard from './SupportInnovationWizard';
import SupportInnovationDetail from './SupportInnovationDetail';
import {
  STATUTS, TYPES, typeInfo, pageName, ticketNum, relativeDate, isClosed,
  copyToClipboard, Icon, JourneyTrack,
} from './SupportInnovationShared';
import './SupportInnovation.css';

// ─── Ligne de demande (vue demandeur) ────────────────────────────────────────
function TicketRow({ d, onOpen }) {
  const t = typeInfo(d.type);
  return (
    <li>
      <button className={`sinv-row sinv-tone-${t.tone}`} onClick={() => onOpen(d.id)}>
        <span className="sinv-row-icon" aria-hidden="true">{t.key === 'support' ? Icon.bug(18) : Icon.idea(18)}</span>
        <span className="sinv-row-main">
          <span className="sinv-row-title">
            {d.non_lu && <span className="sinv-row-nonlu" title="Nouvelle réponse ou nouveau statut" aria-label="Non lu" />}
            {d.titre}
          </span>
          <span className="sinv-row-meta">
            {ticketNum(d.ticket_number)}
            {d.page && <> · {pageName(d.page)}</>}
            {' · '}{relativeDate(d.created_at)}
          </span>
        </span>
        <span className="sinv-row-side">
          <JourneyTrack statut={d.statut} compact />
          {Number(d.nb_commentaires) > 0 && (
            <span className="sinv-row-comments" title={`${d.nb_commentaires} message(s)`}>{Icon.message(14)} {d.nb_commentaires}</span>
          )}
        </span>
      </button>
    </li>
  );
}

// ─── Carte Kanban (vue super-admin) ──────────────────────────────────────────
function AdminCard({ d, onOpen, editMode, selected, onToggleSelect, draggable, onDragStart, onDragEnd, isDragging }) {
  const t = typeInfo(d.type);
  const nbChildren = Number(d.nb_children);
  return (
    <div
      className={`sinv-card sinv-tone-${t.tone}${isDragging ? ' is-dragging' : ''}${selected ? ' is-selected' : ''}`}
      draggable={draggable}
      onDragStart={draggable ? () => onDragStart(d.id) : undefined}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(d.id)}
      onKeyDown={e => e.key === 'Enter' && onOpen(d.id)}
      role="button"
      tabIndex={0}
    >
      <div className="sinv-card-top">
        {editMode && (
          <input
            type="checkbox"
            className="sinv-card-check"
            checked={selected}
            onClick={e => e.stopPropagation()}
            onChange={() => onToggleSelect(d.id)}
            aria-label={`Sélectionner ${ticketNum(d.ticket_number)}`}
          />
        )}
        <span className="sinv-card-type">{t.key === 'support' ? Icon.bug(13) : Icon.idea(13)} {t.label}</span>
        <span className="sinv-num">{ticketNum(d.ticket_number)}</span>
      </div>
      <div className="sinv-card-title">{d.titre}</div>
      <div className="sinv-card-meta">
        <span>{d.auteur_prenom} {d.auteur_nom}</span>
        {d.page && <span>{pageName(d.page)}</span>}
      </div>
      <div className="sinv-card-foot">
        <span>{relativeDate(d.created_at)}</span>
        {d.scope_override && <span className="sinv-card-flag" title="Signalée « hors CRM » par le contrôle, confirmée par l’auteur">{Icon.monitor(13)}</span>}
        {Number(d.nb_commentaires) > 0 && <span>{Icon.message(13)} {d.nb_commentaires}</span>}
        {nbChildren > 0 && <span title="Sous-tickets terminés">🔗 {d.nb_children_done}/{nbChildren}</span>}
      </div>
    </div>
  );
}

// ─── Composant principal ─────────────────────────────────────────────────────
export default function SupportInnovation({ user, setHeaderAction, editMode = false, initialTicketId = null }) {
  const isSuperAdmin = user?.role === 'super-admin';

  const [tab, setTab] = useState(initialTicketId ? 'mine' : 'home'); // home | mine | all
  const [wizard, setWizard] = useState(null);       // { type, parentId } | null
  const [parentContext, setParentContext] = useState(null); // id du ticket maître lors d'une création de sous-ticket
  const [detailId, setDetailId] = useState(initialTicketId);
  const [detailFrom, setDetailFrom] = useState(initialTicketId ? (isSuperAdmin ? 'all' : 'mine') : 'mine');

  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [accessiblePages, setAccessiblePages] = useState([]);

  // Filtres « Mes demandes »
  const [mineFilter, setMineFilter] = useState('open'); // open | closed | all

  // Filtres et vue « Toutes les demandes » (super-admin)
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPage, setFilterPage] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [adminView, setAdminView] = useState('kanban'); // kanban | page
  const [collapsedCols, setCollapsedCols] = useState(() => new Set(['resolu', 'refuse']));
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [promptModal, setPromptModal] = useState(null);

  // Drag & drop (kanban)
  const draggingId = useRef(null);
  const [draggingCard, setDraggingCard] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  useEffect(() => {
    // Seules les pages que l'utilisateur voit dans son menu sont proposées à
    // l'étape « Où ? » (super-admin : toutes, sauf celles masquées sur son compte).
    permissionsApi.getMyPages()
      .then(r => {
        const masquees = new Set(r.data.masquees || []);
        const ids = new Set(r.data.all ? PAGES.map(p => p.id) : (r.data.pages || []));
        setAccessiblePages(PAGES.filter(p => ids.has(p.id) && !masquees.has(p.id)));
      })
      .catch(() => setAccessiblePages([]));
  }, []);

  const loadDemandes = useCallback(async () => {
    setLoadError(false);
    try {
      const r = await supportInnovationApi.getAll({});
      setDemandes(r.data.demandes || []);
    } catch (err) {
      console.error('[Support] chargement:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadDemandes(); }, [loadDemandes]);

  // Le super-admin voit toutes les demandes ; « Mes demandes » reste personnelle
  const mine = useMemo(() => demandes.filter(d => d.user_id === user?.id), [demandes, user]);

  // ── Navigation (URL partageable ?page=support-innovation&ticket=<id>) ──
  const setTicketParam = (id) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', 'support-innovation');
    if (id) params.set('ticket', String(id)); else params.delete('ticket');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };
  const openTicket = useCallback((id) => {
    setDetailFrom(prev => (wizard ? 'mine' : (tab === 'home' ? 'mine' : tab)) || prev);
    setWizard(null);
    setDetailId(id);
    setTicketParam(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [tab, wizard]);
  const closeTicket = useCallback(() => {
    setDetailId(null);
    setTab(detailFrom);
    setTicketParam(null);
  }, [detailFrom]);
  const goTab = (next) => {
    setWizard(null);
    if (detailId) { setDetailId(null); setTicketParam(null); }
    if (next !== 'home') setParentContext(null);
    setTab(next);
  };
  const startWizard = (type) => {
    setWizard({ type, parentId: parentContext });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Bouton d'en-tête : retour vers les deux chemins depuis les autres vues
  const onHome = tab === 'home' && !wizard && !detailId;
  useEffect(() => {
    if (!setHeaderAction) return;
    setHeaderAction(onHome ? null : {
      primary: { label: '+ Nouvelle demande', onClick: () => { setWizard(null); setDetailId(null); setTicketParam(null); setTab('home'); } },
    });
  }, [setHeaderAction, onHome]);

  // ── Super-admin : filtres, kanban, sélection ──
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const digits = q.replace(/\D/g, '');
    return demandes.filter(d => {
      if (filterType && d.type !== filterType) return false;
      if (filterPage && (d.page || '__none__') !== filterPage) return false;
      if (q) {
        const hay = `${d.titre} ${d.description || ''} ${d.auteur_prenom || ''} ${d.auteur_nom || ''}`.toLowerCase();
        const num = String(d.ticket_number || '').padStart(5, '0');
        if (!hay.includes(q) && !(digits && num.includes(digits))) return false;
      }
      return true;
    });
  }, [demandes, search, filterType, filterPage]);
  const statutCounts = useMemo(() => {
    const c = {};
    filtered.forEach(d => { const k = d.statut === 'planifie' ? 'en_cours' : d.statut; c[k] = (c[k] || 0) + 1; });
    return c;
  }, [filtered]);
  const visibleAdmin = filterStatut ? filtered.filter(d => (d.statut === 'planifie' ? 'en_cours' : d.statut) === filterStatut) : filtered;
  const usedPages = useMemo(() => {
    const ids = [...new Set(demandes.map(d => d.page).filter(Boolean))];
    return ids.sort((a, b) => pageName(a).localeCompare(pageName(b), 'fr'));
  }, [demandes]);

  const handleDrop = async (targetStatut) => {
    const id = draggingId.current;
    draggingId.current = null;
    setDraggingCard(null);
    setDragOverCol(null);
    const card = demandes.find(d => d.id === id);
    if (!card || card.statut === targetStatut) return;
    setDemandes(prev => prev.map(d => d.id === id ? { ...d, statut: targetStatut } : d));
    try {
      await supportInnovationApi.update(id, { statut: targetStatut });
    } catch (err) {
      console.error('[Support] déplacement:', err);
      setDemandes(prev => prev.map(d => d.id === id ? { ...d, statut: card.statut } : d));
    }
  };

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  useEffect(() => { if (!editMode) setSelectedIds(new Set()); }, [editMode]);

  const deleteSelection = async () => {
    const ids = [...selectedIds];
    if (!ids.length || !window.confirm(`Supprimer définitivement ${ids.length} demande(s) ?`)) return;
    const failed = [];
    for (const id of ids) {
      try { await supportInnovationApi.remove(id); } catch { failed.push(id); }
    }
    if (failed.length) alert(`${failed.length} demande(s) n’ont pas pu être supprimées.`);
    setSelectedIds(new Set());
    loadDemandes();
  };

  const openGroupPrompt = async (label, list, pageSlug) => {
    if (promptModal?.loading) return;
    setPromptModal({ label, loading: true, text: '', error: '', copied: false });
    try {
      const res = await supportInnovationApi.groupPrompt({
        ids: list.map(d => d.id), group_type: 'page', group_label: label, page_slug: pageSlug || '',
      });
      const text = res.data?.prompt || '';
      await copyToClipboard(text);
      setPromptModal({ label, loading: false, text, error: '', copied: true });
    } catch (err) {
      setPromptModal({ label, loading: false, text: '', error: err.response?.data?.error || 'Génération indisponible.', copied: false });
    }
  };

  // ── Rendu ──
  const firstName = user?.prenom || '';
  const recent = mine.slice(0, 5);
  // Sans aucune demande, l'accueil se recentre sur le seul choix Bug / Amélioration
  const homeSolo = !loading && mine.length === 0;
  const mineOpen = mine.filter(d => !isClosed(d.statut));
  const mineClosed = mine.filter(d => isClosed(d.statut));
  const newCount = demandes.filter(d => d.statut === 'nouveau').length;
  const mineShown = mineFilter === 'open' ? mineOpen : mineFilter === 'closed' ? mineClosed : mine;

  const renderHome = () => (
    <section className={`sinv-home${homeSolo ? ' sinv-home--solo' : ''}`}>
      {parentContext && (
        <p className="sinv-callout sinv-callout--info">
          Création d’un sous-ticket rattaché à {ticketNum(demandes.find(d => d.id === parentContext)?.ticket_number)}.
          <button className="sinv-link sinv-link--small" onClick={() => setParentContext(null)}>Annuler</button>
        </p>
      )}
      <div className="sinv-home-grid">
      <div className="sinv-home-main">
      <div className="sinv-hello">
        <h2>{firstName ? `Bonjour ${firstName}, ` : ''}que souhaitez-vous nous dire&nbsp;?</h2>
        <p>Deux minutes suffisent. Vous suivrez ensuite la réponse dans « Mes demandes ».</p>
      </div>

      <div className="sinv-paths">
        <button className="sinv-path sinv-tone-bug" onClick={() => startWizard('support')}>
          <span className="sinv-path-art" aria-hidden="true">{Icon.bug(34)}</span>
          <span className="sinv-path-kicker">Remonter un bug</span>
          <span className="sinv-path-title">Quelque chose ne marche pas</span>
          <span className="sinv-path-text">Un bouton qui ne répond pas, un message d’erreur, une information fausse…</span>
          <span className="sinv-path-cta">Signaler un bug {Icon.arrowRight(18)}</span>
        </button>
        <button className="sinv-path sinv-tone-idea" onClick={() => startWizard('evolution')}>
          <span className="sinv-path-art" aria-hidden="true">{Icon.idea(34)}</span>
          <span className="sinv-path-kicker">Proposer une amélioration</span>
          <span className="sinv-path-title">J’ai une idée pour A97</span>
          <span className="sinv-path-text">Un filtre en plus, un écran plus simple, une tâche à automatiser… ou même un tout nouvel outil.</span>
          <span className="sinv-path-cta">Proposer une idée {Icon.arrowRight(18)}</span>
        </button>
      </div>

      <p className="sinv-it-note">
        {Icon.monitor(18)}
        <span>Souris, écran, imprimante, ordinateur, messagerie&nbsp;? Ces demandes vont au <strong>support informatique</strong>, pas ici.</span>
      </p>
      </div>

      {!homeSolo && (
        <section className="sinv-recent" aria-labelledby="sinv-recent-title">
          <div className="sinv-recent-head">
            <h3 id="sinv-recent-title">Vos dernières demandes</h3>
            {mine.length > 0 && <button className="sinv-link" onClick={() => goTab('mine')}>Tout voir {Icon.arrowRight(14)}</button>}
          </div>
          {loading ? (
            <div className="sinv-skeleton sinv-skeleton--block" />
          ) : (
            <ul className="sinv-rows">{recent.map(d => <TicketRow key={d.id} d={d} onOpen={openTicket} />)}</ul>
          )}
        </section>
      )}
      </div>
    </section>
  );

  const renderMine = () => {
    if (loading) return <div className="sinv-columns"><div className="sinv-skeleton sinv-skeleton--block" /><div className="sinv-skeleton sinv-skeleton--block" /></div>;
    if (loadError) return <div className="sinv-empty"><p>Impossible de charger vos demandes.</p><button className="sinv-btn sinv-btn--soft" onClick={loadDemandes}>Réessayer</button></div>;
    if (mine.length === 0) {
      return (
        <div className="sinv-empty sinv-empty--big">
          <h3>Vous n’avez encore rien envoyé</h3>
          <p>Un bug qui vous agace ? Une idée qui vous ferait gagner du temps ? C’est par ici.</p>
          <button className="sinv-btn sinv-btn--primary" onClick={() => goTab('home')}>Faire une demande {Icon.arrowRight(18)}</button>
        </div>
      );
    }
    return (
      <>
        <div className="sinv-chips" role="group" aria-label="Filtrer mes demandes">
          {[
            { k: 'open', label: 'En cours', n: mineOpen.length },
            { k: 'closed', label: 'Terminées', n: mineClosed.length },
            { k: 'all', label: 'Toutes', n: mine.length },
          ].map(c => (
            <button key={c.k} className={`sinv-chip${mineFilter === c.k ? ' is-active' : ''}`} aria-pressed={mineFilter === c.k} onClick={() => setMineFilter(c.k)}>
              {c.label} <span>{c.n}</span>
            </button>
          ))}
        </div>
        <div className="sinv-columns">
          {Object.values(TYPES).map(tp => {
            const list = mineShown.filter(d => d.type === tp.key);
            return (
              <section key={tp.key} className={`sinv-column sinv-tone-${tp.tone}`} aria-labelledby={`sinv-col-${tp.key}`}>
                <header className="sinv-column-head">
                  <span className="sinv-column-icon" aria-hidden="true">{tp.key === 'support' ? Icon.bug(20) : Icon.idea(20)}</span>
                  <h3 id={`sinv-col-${tp.key}`}>{tp.plural}</h3>
                  <span className="sinv-count">{list.length}</span>
                </header>
                {list.length > 0 ? (
                  <ul className="sinv-rows">{list.map(d => <TicketRow key={d.id} d={d} onOpen={openTicket} />)}</ul>
                ) : (
                  <p className="sinv-column-empty">
                    {mineFilter === 'open'
                      ? (tp.key === 'support' ? 'Aucun bug en cours.' : 'Aucune idée en cours.')
                      : 'Rien ici pour le moment.'}
                  </p>
                )}
              </section>
            );
          })}
        </div>
      </>
    );
  };

  const renderAll = () => {
    const byPage = {};
    visibleAdmin.forEach(d => { const k = d.page || '__none__'; (byPage[k] = byPage[k] || []).push(d); });
    const pageKeys = Object.keys(byPage).sort((a, b) => (a === '__none__') - (b === '__none__') || pageName(a).localeCompare(pageName(b), 'fr'));
    return (
      <>
        <div className="page-toolbar">
          <input className="page-search" placeholder="🔍 Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className={`page-select${filterType ? ' active-filter' : ''}`} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">Bugs et améliorations</option>
            {Object.values(TYPES).map(tp => <option key={tp.key} value={tp.key}>{tp.plural}</option>)}
          </select>
          <select className={`page-select${filterPage ? ' active-filter' : ''}`} value={filterPage} onChange={e => setFilterPage(e.target.value)}>
            <option value="">Toutes les pages</option>
            {usedPages.map(p => <option key={p} value={p}>{pageName(p)}</option>)}
            <option value="__none__">Page non précisée</option>
          </select>
        </div>

        <div className="sinv-admin-bar">
          <div className="sinv-chips" role="group" aria-label="Filtrer par statut">
            <button className={`sinv-chip${!filterStatut ? ' is-active' : ''}`} aria-pressed={!filterStatut} onClick={() => setFilterStatut('')}>
              Tous <span>{filtered.length}</span>
            </button>
            {STATUTS.map(s => (
              <button key={s.value} className={`sinv-chip${filterStatut === s.value ? ' is-active' : ''}`} aria-pressed={filterStatut === s.value}
                style={{ '--statut-color': s.color }} onClick={() => setFilterStatut(filterStatut === s.value ? '' : s.value)}>
                <span className="sinv-dot" />{s.label} <span>{statutCounts[s.value] || 0}</span>
              </button>
            ))}
          </div>
          <div className="sinv-segmented" role="group" aria-label="Affichage">
            <button className={adminView === 'kanban' ? 'is-active' : ''} aria-pressed={adminView === 'kanban'} onClick={() => setAdminView('kanban')}>Par statut</button>
            <button className={adminView === 'page' ? 'is-active' : ''} aria-pressed={adminView === 'page'} onClick={() => setAdminView('page')}>Par page</button>
          </div>
        </div>

        {editMode && selectedIds.size > 0 && (
          <div className="sinv-bulk" role="region" aria-label="Sélection">
            <span>{selectedIds.size} demande(s) sélectionnée(s)</span>
            <button className="sinv-link sinv-link--small" onClick={() => setSelectedIds(new Set())}>Tout désélectionner</button>
            <button className="sinv-btn sinv-btn--danger sinv-btn--sm" onClick={deleteSelection}>🗑️ Supprimer la sélection</button>
          </div>
        )}

        {loading && <div className="sinv-skeleton sinv-skeleton--block" />}

        {!loading && adminView === 'kanban' && (
          <div className="sinv-kanban">
            {STATUTS.filter(s => !filterStatut || s.value === filterStatut).map(s => {
              const list = visibleAdmin.filter(d => (d.statut === 'planifie' ? 'en_cours' : d.statut) === s.value);
              const collapsed = collapsedCols.has(s.value) && !filterStatut;
              return (
                <div
                  key={s.value}
                  className={`sinv-kcol${dragOverCol === s.value ? ' is-over' : ''}${collapsed ? ' is-collapsed' : ''}`}
                  style={{ '--statut-color': s.color }}
                  onDragOver={e => { e.preventDefault(); setDragOverCol(s.value); }}
                  onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
                  onDrop={e => { e.preventDefault(); handleDrop(s.value); }}
                >
                  <button className="sinv-kcol-head" aria-expanded={!collapsed}
                    onClick={() => setCollapsedCols(prev => { const n = new Set(prev); n.has(s.value) ? n.delete(s.value) : n.add(s.value); return n; })}>
                    <span className="sinv-dot" />
                    <span className="sinv-kcol-label">{s.label}</span>
                    <span className="sinv-count">{list.length}</span>
                  </button>
                  {!collapsed && (
                    <div className="sinv-kcol-cards">
                      {list.map(d => (
                        <AdminCard
                          key={d.id} d={d} onOpen={openTicket} editMode={editMode}
                          selected={selectedIds.has(d.id)} onToggleSelect={toggleSelect}
                          draggable onDragStart={(id) => { draggingId.current = id; setDraggingCard(id); }}
                          onDragEnd={() => { draggingId.current = null; setDraggingCard(null); setDragOverCol(null); }}
                          isDragging={draggingCard === d.id}
                        />
                      ))}
                      {list.length === 0 && <div className="sinv-kcol-empty">{draggingCard ? 'Déposer ici' : 'Vide'}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && adminView === 'page' && (
          <div className="sinv-pages">
            {pageKeys.length === 0 && <div className="sinv-empty"><p>Aucune demande ne correspond aux filtres.</p></div>}
            {pageKeys.map(k => {
              const label = k === '__none__' ? 'Page non précisée' : pageName(k);
              return (
                <section key={k} className="sinv-page-group">
                  <header className="sinv-page-group-head">
                    <h3>{label}</h3>
                    <span className="sinv-count">{byPage[k].length}</span>
                    <button className="sinv-btn sinv-btn--soft sinv-btn--sm" onClick={() => openGroupPrompt(label, byPage[k], k === '__none__' ? '' : k)}
                      title={`Un prompt Claude Code regroupant ces ${byPage[k].length} demande(s)`}>
                      {Icon.wand(14)} Prompt groupé
                    </button>
                  </header>
                  <div className="sinv-page-group-cards">
                    {byPage[k].map(d => (
                      <AdminCard key={d.id} d={d} onOpen={openTicket} editMode={editMode} selected={selectedIds.has(d.id)} onToggleSelect={toggleSelect} draggable={false} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </>
    );
  };

  const BACK_LABEL = { mine: 'Mes demandes', all: 'Toutes les demandes', home: 'Retour' };

  return (
    <div className="page-container sinv-page">
      <div className="page-tabs">
        <button className={tab === 'home' && !detailId ? 'active' : ''} onClick={() => goTab('home')}>✨ Nouvelle demande</button>
        <button className={(tab === 'mine' && !wizard) ? 'active' : ''} onClick={() => goTab('mine')}>
          📋 Mes demandes {mineOpen.length > 0 && <span className="tab-count">{mineOpen.length}</span>}
        </button>
        {isSuperAdmin && (
          <button className={(tab === 'all' && !wizard) ? 'active' : ''} onClick={() => goTab('all')}>
            📂 Toutes les demandes {newCount > 0 && <span className="tab-count">{newCount}</span>}
          </button>
        )}
      </div>

      {detailId ? (
        <SupportInnovationDetail
          key={detailId}
          id={detailId}
          user={user}
          isSuperAdmin={isSuperAdmin}
          editMode={editMode}
          backLabel={BACK_LABEL[detailFrom] || 'Retour'}
          onBack={closeTicket}
          onChanged={loadDemandes}
          onOpenTicket={openTicket}
          onCreateSubticket={(parentId) => { setParentContext(parentId); setDetailId(null); setTicketParam(null); setTab('home'); }}
          onDeleted={() => { closeTicket(); loadDemandes(); }}
        />
      ) : wizard ? (
        <SupportInnovationWizard
          key={wizard.type}
          type={wizard.type}
          parentTicketId={wizard.parentId}
          accessiblePages={accessiblePages}
          onCancel={() => setWizard(null)}
          onCreated={() => { setParentContext(null); loadDemandes(); }}
          onOpenTicket={openTicket}
          onGoMine={() => goTab('mine')}
          onRestart={() => setWizard(null)}
        />
      ) : tab === 'home' ? renderHome()
        : tab === 'mine' ? renderMine()
        : isSuperAdmin ? renderAll() : null}

      {promptModal && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Prompt groupé · {promptModal.label}</h2>
              {!promptModal.loading && <button className="modal-close" onClick={() => setPromptModal(null)}>✕</button>}
            </div>
            <div className="modal-body">
              {promptModal.loading ? (
                <p className="sinv-help"><span className="sinv-spinner" aria-hidden="true" /> Claude Opus 5.5 rédige le prompt…</p>
              ) : promptModal.error ? (
                <p className="sinv-callout sinv-callout--error" role="alert">{promptModal.error}</p>
              ) : (
                <>
                  <p className="sinv-help">Copié dans le presse-papiers. Vous pouvez l’ajuster avant de le recopier.</p>
                  <textarea className="sinv-prompt-textarea" value={promptModal.text} spellCheck={false}
                    onChange={e => setPromptModal(m => ({ ...m, text: e.target.value, copied: false }))} />
                </>
              )}
            </div>
            {!promptModal.loading && (
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setPromptModal(null)}>Fermer</button>
                {promptModal.text && (
                  <button className="btn-primary" onClick={async () => { await copyToClipboard(promptModal.text); setPromptModal(m => ({ ...m, copied: true })); }}>
                    {promptModal.copied ? '✓ Copié' : 'Copier'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
